import React from "react";
import { Entity } from "../../data/companies";

// ============================================================
// Detail sections (08–15) — har section ka apna trendy design:
// 08 Who We Serve        → mono outline chips
// 09 Expertise           → soft filled pills
// 10 Framework           → numbered vertical timeline
// 11 AI Philosophy       → big statement block (orange rail)
// 12 Why Choose          → arrow-marked reason rows
// 13 Ecosystem Advantage → frosted card paragraph
// 14 Global & Local      → split twin cards
// 15 Partner With Us     → 2-col hairline grid + closing strip
// Content: companies.ts → entity.details
// ============================================================

const SecHead: React.FC<{ num?: string; title: string }> = ({ title }) => (
  <div className="xb-dsec--head wow fadeInUp" data-wow-duration="600ms">
    <h2 className="xb-dsec--title">{title}</h2>
  </div>
);

const CompanyDetails: React.FC<{ entity: Entity }> = ({ entity }) => {
  const d = entity.details;
  if (!d) return null;

  return (
    <section className="xb-details pt-40 pb-100">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            {/* ===== Statement section (e.g. Who We Back) ===== */}
            {d.statement && (
              <div className="xb-dsec">
                <SecHead num={d.statement.num} title={d.statement.title} />
                <div
                  className="xb-dstatement wow fadeInUp"
                  data-wow-duration="700ms"
                >
                  <p>{d.statement.text}</p>
                </div>
              </div>
            )}

            {/* ===== 08 Who We Serve — mono outline chips ===== */}
            {d.serve && (
              <div className="xb-dsec">
                <SecHead num={d.serve.num} title={d.serve.title} />
                <div className="xb-dchips">
                  {d.serve.chips.map((chip, i) => (
                    <span
                      className="xb-dchip xb-dchip--outline wow fadeInUp"
                      data-wow-delay={`${i * 70}ms`}
                      data-wow-duration="500ms"
                      key={chip}
                    >
                      {chip}
                    </span>
                  ))}
                </div>
                {d.serve.note && (
                  <p className="xb-dtimeline--note wow fadeInUp">
                    {d.serve.note}
                  </p>
                )}
              </div>
            )}

            {/* ===== Industries with descriptions (AppForge style) ===== */}
            {d.serveCards && (
              <div className="xb-dsec">
                <SecHead num={d.serveCards.num} title={d.serveCards.title} />
                <div className="row">
                  {d.serveCards.cards.map((card, i) => (
                    <div className="col-md-6" key={card.title}>
                      <div
                        className="xb-dpartner wow fadeInUp"
                        data-wow-delay={`${(i % 2) * 100}ms`}
                        data-wow-duration="600ms"
                      >
                        <h3>{card.title}</h3>
                        <p>{card.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ===== 09 Expertise — soft filled pills ===== */}
            {d.expertise && (
              <div className="xb-dsec">
                <SecHead num={d.expertise.num} title={d.expertise.title} />
                <div className="xb-dchips">
                  {d.expertise.chips.map((chip, i) => (
                    <span
                      className="xb-dchip xb-dchip--fill wow fadeInUp"
                      data-wow-delay={`${i * 70}ms`}
                      data-wow-duration="500ms"
                      key={chip}
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ===== 10 Framework — numbered vertical timeline ===== */}
            {d.framework && (
              <div className="xb-dsec">
                <SecHead num={d.framework.num} title={d.framework.title} />
                {d.framework.intro && (
                  <p className="xb-dtimeline--intro">{d.framework.intro}</p>
                )}
                <div className="xb-dtimeline">
                  {d.framework.steps.map((step, i) => (
                    <div
                      className="xb-dtimeline--step wow fadeInUp"
                      data-wow-delay={`${i * 90}ms`}
                      data-wow-duration="600ms"
                      key={step.title}
                    >
                      <span className="xb-dtimeline--num">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="xb-dtimeline--body">
                        <h3>{step.title}</h3>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                  {d.framework.note && (
                    <p className="xb-dtimeline--note wow fadeInUp">
                      {d.framework.note}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ===== 11 AI Philosophy — statement block ===== */}
            {d.philosophy && (
              <div className="xb-dsec">
                <SecHead num={d.philosophy.num} title={d.philosophy.title} />
                <div
                  className="xb-dstatement wow fadeInUp"
                  data-wow-duration="700ms"
                >
                  <p>{d.philosophy.text}</p>
                </div>
              </div>
            )}

            {/* ===== 12 Why Choose — arrow reason rows ===== */}
            {d.reasons && (
              <div className="xb-dsec">
                <SecHead num={d.reasons.num} title={d.reasons.title} />
                <div className="xb-dreasons">
                  {d.reasons.items.map((item, i) => (
                    <div
                      className="xb-dreason wow fadeInLeft"
                      data-wow-delay={`${i * 90}ms`}
                      data-wow-duration="600ms"
                      key={item.lead}
                    >
                      <span className="xb-dreason--arrow">→</span>
                      <p>
                        <strong>{item.lead}</strong> {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ===== 13 Ecosystem Advantage — frosted card ===== */}
            {d.ecosystem && (
              <div className="xb-dsec">
                <SecHead num={d.ecosystem.num} title={d.ecosystem.title} />
                <div
                  className="xb-dcardpara wow fadeInUp"
                  data-wow-duration="700ms"
                >
                  <p>{d.ecosystem.text}</p>
                </div>
              </div>
            )}

            {/* ===== 14 Global & Local — twin split cards ===== */}
            {d.globalLocal && (
              <div className="xb-dsec">
                <SecHead num={d.globalLocal.num} title={d.globalLocal.title} />
                <div className="row mt-none-30">
                  <div className="col-md-6 mt-30">
                    <div
                      className="xb-dtwin wow fadeInLeft"
                      data-wow-duration="600ms"
                    >
                      <span className="xb-dtwin--tag">Local</span>
                      <p>{d.globalLocal.local}</p>
                    </div>
                  </div>
                  <div className="col-md-6 mt-30">
                    <div
                      className="xb-dtwin xb-dtwin--alt wow fadeInRight"
                      data-wow-duration="600ms"
                    >
                      <span className="xb-dtwin--tag">Global</span>
                      <p>{d.globalLocal.global}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== 15 Partner With Us — hairline grid ===== */}
            {d.partner && (
              <div className="xb-dsec">
                <SecHead num={d.partner.num} title={d.partner.title} />
                <div className="row">
                  {d.partner.cards.map((card, i) => (
                    <div className="col-md-6" key={card.title}>
                      <div
                        className="xb-dpartner wow fadeInUp"
                        data-wow-delay={`${(i % 2) * 100}ms`}
                        data-wow-duration="600ms"
                      >
                        <h3>{card.title}</h3>
                        <p>{card.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="xb-dclosing wow fadeInUp"
                  data-wow-duration="600ms"
                >
                  {d.partner.closing}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyDetails;
